import {
  NutritionTargetDto,
  NutritionTargetType,
  NutritionTargetType2,
  RecomendedTargetBriefInfo,
} from "@growth-machine-llc/stridist-api-client";
import { Box, Grid2, Skeleton, Typography, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import dayjs from "dayjs";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Cell, PolarAngleAxis, RadialBar, RadialBarChart } from "recharts";
import clsx from "clsx";
import { InternalNutritionEntryDto } from "../../utils/chart";
import NutritionTargetsService from "../../services/NutritionTargetsService";
import { CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY } from "./ClientNutritionCard";
import { useOptimisticUpdateMutation } from "../../hooks/useOptimisticUpdateMutation";
import { thousandSeparator } from "./utils/thousandSeparator";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import { JourneyAnchors, nutritionTargetsArray } from "../../constants";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1, 4, 2, 4),
    display: "flex",
    flexWrap: "nowrap",
    width: "90%",
    justifyContent: "center",
    minWidth: "fit-content",

    [theme.breakpoints.down(360)]: {
      flexDirection: "column-reverse",
    },

    [theme.breakpoints.down("md")]: {
      width: "100%",
      minWidth: "unset",
      margin: theme.spacing(1, 1, 2, 1),
    },

    [theme.containerQueries.down("md")]: {
      width: "100%",
      minWidth: "unset",
      margin: theme.spacing(1, 1, 2, 1),
    },
  },

  targetButton: {
    display: "flex",
    width: theme.spacing(23),
    minWidth: "fit-content",
    maxWidth: "100%",
    alignItems: "center",
    textWrap: "nowrap",
    padding: theme.spacing(1, 1, 1, 3),
    margin: theme.spacing(1, -2, 1, 0),
    borderRadius: theme.shape.radius.md,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    cursor: "pointer",
    userSelect: "none",
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.shortest,
    }),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1, 1, 1, 2),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(1, 1, 1, 2),
    },

    "&:hover": {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
    },

    "& svg": {
      marginRight: theme.spacing(0.5),
      marginLeft: "auto",
    },
  },

  helperText: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1,
    color: theme.palette.text.secondary,
  },

  targetType: {
    fontSize: 14,
    fontFamily: theme.typography.body2.fontFamily,
    fontWeight: 500,
    lineHeight: 1.2,

    [theme.breakpoints.down("md")]: {
      fontSize: 12,
    },

    [theme.containerQueries.down("md")]: {
      fontSize: 12,
    },
  },

  targetValue: {
    fontSize: 20,
    fontFamily: theme.typography.body2.fontFamily,
    fontWeight: 700,
    lineHeight: 1.2,

    [theme.breakpoints.down("md")]: {
      fontSize: 16,
    },

    [theme.containerQueries.down("md")]: {
      fontSize: 16,
    },
  },

  selected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    width: theme.spacing(27),

    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.background.default,
    },

    "& svg": {
      marginRight: theme.spacing(0.5),
      marginLeft: "auto",
    },
  },

  noInteraction: {
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.background.default,
    },
  },

  grayscale: {
    filter: "grayscale(100%)",
    pointerEvents: "none",
    cursor: "default",
  },

  chartContainer: {
    alignSelf: "center",
    width: "max-content",
    fontFamily: "sans-serif",
    margin: theme.spacing(3, 0, 0, 2),
    paddingRight: theme.spacing(0),

    [theme.breakpoints.down(360)]: {
      margin: 0,
      paddingLeft: theme.spacing(5),
    },

    [theme.breakpoints.down("md")]: {
      margin: theme.spacing(-2, 0, 0, -1),
      paddingRight: theme.spacing(4),
    },

    [theme.containerQueries.down("md")]: {
      margin: theme.spacing(-2, 0, 0, -1),
      paddingRight: theme.spacing(4),
    },
  },

  smUp: {
    display: "block",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },

    [theme.containerQueries.down("sm")]: {
      display: "none",
    },
  },

  smDown: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "block",
    },

    [theme.containerQueries.down("sm")]: {
      display: "block",
    },
  },
}));

const Chart = ({
  smUp,
  data,
}: {
  smUp?: boolean;
  data: InternalNutritionEntryDto;
}) => {
  const theme = useTheme();

  const processedData = useMemo(
    () => [
      {
        name: "Calories",
        value:
          data?.targetCalories !== undefined
            ? data?.calories
            : data?.calories || 0.75,
        overflow:
          data?.targetCalories && data?.calories > data?.targetCalories
            ? data?.calories - data?.targetCalories
            : 0,
      },
    ],
    [data],
  );

  //animation timing detection

  const [prevValue, setPrevValue] = useState(null);

  useEffect(() => {
    setPrevValue(data);
  }, [data]);

  const hadOverflow = useMemo(
    () => prevValue && prevValue.calories > prevValue.targetCalories,
    [prevValue],
  );

  const targetCompletion =
    data &&
    Math.round(
      (data.targetCalories > 0 || !data.calories
        ? data.calories / data.targetCalories
        : 1) * 100,
    );

  return (
    <RadialBarChart
      cx="50%"
      cy="50%"
      height={smUp ? 200 : 170}
      width={smUp ? 200 : 170}
      data={processedData}
      margin={{ top: -500, right: 0, left: 0, bottom: -50 }}
      barSize={12}
      innerRadius="100%"
      startAngle={90}
      endAngle={-270}
      barGap={-12}
    >
      <defs>
        <pattern
          id="dots"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <circle cx="2" cy="2" r="2" fill={theme.palette.primary.main} />
        </pattern>
      </defs>

      <PolarAngleAxis
        type="number"
        domain={[0, data?.targetCalories || 1]}
        tick={false}
        angleAxisId={0}
      />

      <PolarAngleAxis
        type="number"
        domain={[0, data?.targetCalories || 1]}
        tick={false}
        angleAxisId={1}
      />

      {data?.targetId !== undefined ? (
        <>
          <text
            x="50%"
            y="45%"
            fontSize={smUp ? 30 : 24}
            fontWeight="bold"
            textAnchor="middle"
            fill={theme.palette.text.primary}
          >
            {targetCompletion < Infinity ? `${targetCompletion}%` : "0%"}
          </text>

          <text
            x="50%"
            y="56%"
            fontSize={smUp ? 18 : 14}
            textAnchor="middle"
            fill={theme.palette.text.secondary}
          >
            of daily goal
          </text>

          <text
            x="50%"
            y="66%"
            fontSize={smUp ? 18 : 14}
            textAnchor="middle"
            fill={theme.palette.text.primary}
          >
            {thousandSeparator(data.calories)} kcal
          </text>
        </>
      ) : data ? (
        <>
          <text
            x="50%"
            y="48%"
            fontSize={smUp ? 26 : 18}
            fontWeight="bold"
            textAnchor="middle"
            fill={theme.palette.text.primary}
          >
            {thousandSeparator(data.calories)} kcal
          </text>

          <text
            x="50%"
            y="60%"
            fontSize={smUp ? 16 : 14}
            textAnchor="middle"
            fill={theme.palette.text.secondary}
          >
            tracking only
          </text>
        </>
      ) : (
        <>
          <text
            x="50%"
            y="45%"
            fontSize={smUp ? 32 : 24}
            fontWeight="bold"
            textAnchor="middle"
            fill={theme.palette.text.primary}
          >
            -- %
          </text>

          <text
            x="50%"
            y="56%"
            fontSize={smUp ? 18 : 14}
            textAnchor="middle"
            fill={theme.palette.text.secondary}
          >
            of daily goal
          </text>

          <text
            x="50%"
            y="66%"
            fontSize={smUp ? 18 : 14}
            textAnchor="middle"
            fill={theme.palette.text.primary}
          >
            - kcal
          </text>
        </>
      )}

      <RadialBar
        dataKey="value"
        fill={theme.palette.primary.main}
        cornerRadius={10}
        fillOpacity={0.6}
        stroke={theme.palette.primary.main}
        strokeWidth={2}
        angleAxisId={0}
        stackId={1}
        animationDuration={500}
        animationBegin={hadOverflow ? 500 : 0}
        animationEasing="linear"
        background={{
          fill: theme.palette.grey[100],
          strokeWidth: 3,
          stroke: theme.palette.background.default,
        }}
      />

      <RadialBar
        dataKey="overflow"
        cornerRadius={10}
        stroke={theme.palette.primary.main}
        strokeWidth={2}
        strokeLinecap="round"
        angleAxisId={1}
        stackId={1}
        animationBegin={!hadOverflow ? 500 : 0}
        animationDuration={500}
        animationEasing="linear"
      >
        <Cell key="cell-0" fill="url(#dots)" />
      </RadialBar>
    </RadialBarChart>
  );
};

const getTargetLabel = (type: string) =>
  nutritionTargetsArray.find((t) => t.value === (type || "ALL_DAYS"))?.label;

const TargetButton = ({
  clickEnabled,
  selected,
  target,
  handleSelect,
  hasTargets,
}: {
  clickEnabled: boolean;
  selected: boolean;
  handleSelect?: () => void;
  target?: NutritionTargetDto;
  hasTargets?: boolean;
}) => {
  const s = useStyles();

  const targetTypeLabel = React.useMemo(
    () => getTargetLabel(target?.targetType)?.toLowerCase(),
    [target],
  );

  return (
    <Box
      onClick={() => clickEnabled && !selected && handleSelect()}
      className={clsx(
        s.targetButton,
        selected && s.selected,
        !clickEnabled && s.noInteraction,
      )}
    >
      <Box>
        <div className={s.targetType}>{targetTypeLabel}</div>
        <div className={s.targetValue}>
          {target
            ? `${hasTargets ? thousandSeparator(Math.round(target.calories)) : "--"} kcal`
            : "tracking"}
        </div>
      </Box>
      {clickEnabled &&
        (selected ? <ChevronRight size={25} /> : <ChevronDown size={25} />)}
    </Box>
  );
};

interface ClientNutritionDayChartProps {
  data?: InternalNutritionEntryDto;

  readonly: boolean;
  targets?: NutritionTargetDto[];
  client?: { clientId?: number; username?: string };
}

export const ClientNutritionDayChart = (
  props: ClientNutritionDayChartProps,
) => {
  const { data, targets, client, readonly } = props;

  const { showToastAlert } = useToastAlert();

  const filteredTargets = useMemo(
    () =>
      targets?.filter(
        (target) =>
          (!target.expiredAt || target.expiredAt.isAfter(data?.date, "day")) &&
          !target.date.isAfter(data?.date, "day"),
      ) ||
      (readonly
        ? [{ targetType: "TRAINING_DAYS" }, { targetType: "REST_DAYS" }]
        : [{ targetType: "ALL_DAYS" }]
      ).map(NutritionTargetDto.fromJS),
    [targets, readonly, data],
  );

  const selectedTarget = useMemo(
    () =>
      data
        ? filteredTargets.find((target) => target.id === data?.targetId)
        : filteredTargets[0],
    [data, filteredTargets],
  );

  const { username } = client || {};

  const { mutate: updateTarget } = useOptimisticUpdateMutation({
    mutationFn: NutritionTargetsService.setPreferedTarget,
    queryKey: [CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY, { username }],
    exactQueryKey: false,
    optimisticUpdater: {
      updateFn: (oldData: RecomendedTargetBriefInfo[], vars) => {
        if (!oldData) return oldData;
        const index = oldData.findIndex((entry) =>
          entry.date.isSame(data.date, "day"),
        );

        const targetId = filteredTargets.find(
          (target) =>
            NutritionTargetType2[target.targetType] === vars.preferredType,
        )?.id;

        if (index !== -1) {
          oldData[index].recomendedTargetId = targetId;
        } else {
          oldData.push(
            RecomendedTargetBriefInfo.fromJS({
              date: data.date,
              recomendedTargetId: targetId,
            }),
          );
        }

        return [
          ...oldData.map((entry) => RecomendedTargetBriefInfo.fromJS(entry)),
        ];
      },
    },
    options: {
      onSuccess: (_, vars) => {
        showToastAlert("success", {
          message: `Saved ${getTargetLabel(Object.values(NutritionTargetType)[vars.preferredType])} target for ${dayjs(data.date).format("MMM D")}`,
        });
      },
    },
    disableToastAlerts: true,
  });

  const handleSelectTarget = useCallback(
    (target: NutritionTargetDto) => {
      const date = dayjs(data.date);
      username &&
        updateTarget({
          username,
          year: date.year(),
          month: date.month() + 1,
          day: date.date(),
          preferredType: NutritionTargetType2[target.targetType],
        });
    },
    [username, data, updateTarget],
  );

  const selectedTargetIndex = filteredTargets?.findIndex(
    (target) => selectedTarget && target.id === selectedTarget.id,
  );

  const s = useStyles();
  const theme = useTheme();

  return (
    <Grid2 container spacing={2} className={clsx(s.root, !data && s.grayscale)}>
      <Grid2 className={JourneyAnchors.CLIENT_NUTRITIONS_TARGET_INFO}>
        {data?.date ? (
          <Typography variant="subtitle2" color="textSecondary">
            {dayjs(data.date).format("MMM D, YYYY")}
          </Typography>
        ) : (
          <Skeleton
            variant="text"
            width={100}
            sx={{ visibility: readonly ? "hidden" : "visible" }}
          />
        )}
        <Typography variant="h5">Target</Typography>
        {filteredTargets && filteredTargets.length > 0 ? (
          <TargetButton
            selected={selectedTargetIndex === 0}
            clickEnabled={filteredTargets?.length > 0}
            target={filteredTargets[0]}
            handleSelect={() => handleSelectTarget(filteredTargets[0])}
            hasTargets={Boolean(targets)}
          />
        ) : (
          <TargetButton
            selected
            clickEnabled={false}
            hasTargets={Boolean(targets)}
          />
        )}
        {filteredTargets && filteredTargets.length > 1 && (
          <TargetButton
            selected={selectedTargetIndex === 1}
            clickEnabled
            target={filteredTargets[1]}
            handleSelect={() => handleSelectTarget(filteredTargets[1])}
            hasTargets={Boolean(targets)}
          />
        )}
        {filteredTargets.length > 1 && (
          <Typography className={s.helperText}>
            Choose target type that
            <br /> matches your day.
          </Typography>
        )}
      </Grid2>
      <Grid2 className={clsx(s.chartContainer, s.smUp)}>
        <Chart smUp data={data} />
      </Grid2>
      <Grid2 className={clsx(s.chartContainer, s.smDown)}>
        <Chart data={data} />
      </Grid2>
    </Grid2>
  );
};
